/**
 * Chart.js Range Selector Plugin
 *
 * Draws two draggable handles and a selection window on a "navigator" chart.
 * The unselected regions are overlaid with a semi-transparent color.
 * Users can:
 *   - Drag the left/right handles to resize the range
 *   - Drag the center window to pan (without resizing)
 *   - Double-click the chart to reset the range to the full data extent
 *
 * Plugin options (set via chart.options.plugins.rangeSelector):
 *   enabled: boolean
 *   overlayColor: string          (default 'rgba(0,0,0,0.15)')
 *   handleColor: string           (default '#2388FF')
 *   handleWidth: number           (default 8, pixels)
 *   windowBorderColor: string     (default '#2388FF')
 *   onRangeChange: (min, max) => void
 *   onRangeReset: () => void
 * 
 * Implementation guide:
 *
 *  1. Architecture: Two-chart layout
 *     - A main chart (tall) shows the data for the selected range.
 *     - A navigator chart (thin, ~60px) below it always shows the full dataset.
 *     - This plugin runs on the navigator chart only.
 *
 *  2. Setup in a component (Angular + ng2-charts)
 *     a) Import the plugin:
 *        import { RangeSelectorPlugin } from '../plugins/chartjs-plugin-range-selector';
 *
 *     b) Register it as a plugin for the navigator chart only:
 *        navigatorPlugins = [RangeSelectorPlugin];
 *
 *     c) Configure the navigator chart options:
 *        navigatorChartOptions = {
 *          responsive: true,
 *          maintainAspectRatio: false,
 *          animation: false,
 *          scales: { x: { ticks: { display: false }, grid: { display: false } },
 *                    y: { ticks: { display: false }, grid: { display: false } } },
 *          plugins: {
 *            tooltip: { enabled: false },
 *            legend: { display: false },
 *            rangeSelector: {
 *              enabled: true,
 *              onRangeChange: (min, max) => { ... update main chart x.min/x.max ... },
 *              onRangeReset: () => { ... reset main chart to full range ... }
 *            }
 *          }
 *        };
 *
 *     d) In the template, add the navigator canvas below the main chart:
 *        <div class="navigator-wrapper" style="height:60px">
 *          <canvas baseChart [data]="navigatorChartData"
 *            [options]="navigatorChartOptions" [plugins]="navigatorPlugins" type="line">
 *          </canvas>
 *        </div>
 *
 *  3. Updating the main chart range
 *     In onRangeChange, set min/max on both the bound chartOptions object
 *     AND the live Chart.js instance to survive Angular re-renders:
 *        this.chartOptions.scales.x.min = min;
 *        this.chartOptions.scales.x.max = max;
 *        this.chart.chart.options.scales['x'].min = min;
 *        this.chart.chart.options.scales['x'].max = max;
 *        this.chart.chart.update('none');
 *
 *  4. Synchronizing multiple charts
 *     - Define a signal per group: rangeSignal = signal<{min,max}|null>(null)
 *     - Each chart receives the signal as [selectedRange]="rangeSignal()"
 *     - Each chart emits (rangeChange) which updates the signal
 *     - Use a _suppressRangeEmit flag to avoid feedback loops
 *
 *  5. Programmatic API (static methods on RangeSelectorPlugin)
 *     - RangeSelectorPlugin.setRange(chart, min, max)  — set range visually
 *     - RangeSelectorPlugin.resetRange(chart)           — reset to full extent
 */

export interface RangeSelectorOptions {
  enabled: boolean;
  overlayColor?: string;
  handleColor?: string;
  handleWidth?: number;
  windowBorderColor?: string;
  onRangeChange?: (min: number, max: number) => void;
  onRangeReset?: () => void;
}

const DEFAULT_OPTIONS: Required<Omit<RangeSelectorOptions, 'onRangeChange'>> & { onRangeChange: null } = {
  enabled: false,
  overlayColor: 'rgba(0,0,0,0.15)',
  handleColor: '#2388FF',
  handleWidth: 8,
  windowBorderColor: '#2388FF',
  onRangeChange: null,
  onRangeReset: null,
};

type DragTarget = 'left' | 'right' | 'window' | null;

interface RangeSelectorState {
  /** Current selected range in data-space (x values) */
  rangeMin: number | null;
  rangeMax: number | null;
  /** Drag state */
  dragTarget: DragTarget;
  dragStartX: number; // pixel
  dragStartRangeMin: number;
  dragStartRangeMax: number;
  /** Full data extent */
  dataMin: number | null;
  dataMax: number | null;
}

function getOpt<K extends keyof RangeSelectorOptions>(
  chart: any,
  key: K
): Required<RangeSelectorOptions>[K] {
  const pluginOpts = chart.options?.plugins?.rangeSelector;
  if (pluginOpts && pluginOpts[key] !== undefined) {
    return pluginOpts[key];
  }
  return (DEFAULT_OPTIONS as any)[key];
}

function getXScale(chart: any) {
  if (!chart.data.datasets.length) return null;
  const meta = chart.getDatasetMeta(0);
  return chart.scales[meta.xAxisID];
}

function getYScale(chart: any) {
  if (!chart.data.datasets.length) return null;
  const meta = chart.getDatasetMeta(0);
  return chart.scales[meta.yAxisID];
}

function getState(chart: any): RangeSelectorState {
  return chart._rangeSelectorState;
}

function computeDataExtent(chart: any): { min: number; max: number } | null {
  let globalMin = Infinity;
  let globalMax = -Infinity;
  for (const ds of chart.data.datasets) {
    for (const pt of ds.data) {
      const x = pt.x !== undefined ? pt.x : pt;
      if (typeof x === 'number' || x instanceof Date) {
        const val = x instanceof Date ? x.getTime() : x;
        if (val < globalMin) globalMin = val;
        if (val > globalMax) globalMax = val;
      }
    }
  }
  if (globalMin === Infinity) return null;
  return { min: globalMin, max: globalMax };
}

/** Pixel x for a given handle */
function handlePixelX(chart: any, value: number): number {
  const xScale = getXScale(chart);
  return xScale ? xScale.getPixelForValue(value) : 0;
}

/** Which drag target is under the mouse at pixel (px, py)? */
function hitTest(chart: any, px: number, py: number): DragTarget {
  const state = getState(chart);
  if (state.rangeMin === null || state.rangeMax === null) return null;

  const handleWidth = getOpt(chart, 'handleWidth');
  const halfHandle = handleWidth / 2;

  const leftPx = handlePixelX(chart, state.rangeMin);
  const rightPx = handlePixelX(chart, state.rangeMax);

  const yScale = getYScale(chart);
  if (!yScale) return null;

  const top = yScale.top;
  const bottom = yScale.bottom;

  // Must be within the chart area vertically
  if (py < top || py > bottom) return null;

  // Check left handle
  if (Math.abs(px - leftPx) <= halfHandle + 4) return 'left';

  // Check right handle
  if (Math.abs(px - rightPx) <= halfHandle + 4) return 'right';

  // Check window (between handles)
  if (px > leftPx + halfHandle && px < rightPx - halfHandle) return 'window';

  return null;
}

function setCursorStyle(chart: any, cursor: string) {
  if (chart.canvas) {
    chart.canvas.style.cursor = cursor;
  }
}

export const RangeSelectorPlugin = {
  id: 'rangeSelector',

  afterInit(chart: any) {
    if (!getOpt(chart, 'enabled')) return;

    chart._rangeSelectorState = {
      rangeMin: null,
      rangeMax: null,
      dragTarget: null,
      dragStartX: 0,
      dragStartRangeMin: 0,
      dragStartRangeMax: 0,
      dataMin: null,
      dataMax: null,
    } as RangeSelectorState;

    // Bind event handlers
    const canvas = chart.canvas;

    chart._rs_onMouseDown = (e: MouseEvent) => {
      if (!getOpt(chart, 'enabled')) return;
      const state = getState(chart);
      const rect = canvas.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;

      const target = hitTest(chart, px, py);
      if (target) {
        state.dragTarget = target;
        state.dragStartX = px;
        state.dragStartRangeMin = state.rangeMin!;
        state.dragStartRangeMax = state.rangeMax!;
        e.preventDefault();
        e.stopPropagation();
      }
    };

    chart._rs_onMouseMove = (e: MouseEvent) => {
      if (!getOpt(chart, 'enabled')) return;
      const state = getState(chart);
      const rect = canvas.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;

      if (state.dragTarget) {
        const xScale = getXScale(chart);
        if (!xScale) return;

        const currentValue = xScale.getValueForPixel(px);
        const startValue = xScale.getValueForPixel(state.dragStartX);
        const delta = currentValue - startValue;

        const dataMin = state.dataMin!;
        const dataMax = state.dataMax!;

        if (state.dragTarget === 'left') {
          let newMin = state.dragStartRangeMin + delta;
          newMin = Math.max(dataMin, Math.min(newMin, state.rangeMax! - (dataMax - dataMin) * 0.01));
          state.rangeMin = newMin;
        } else if (state.dragTarget === 'right') {
          let newMax = state.dragStartRangeMax + delta;
          newMax = Math.min(dataMax, Math.max(newMax, state.rangeMin! + (dataMax - dataMin) * 0.01));
          state.rangeMax = newMax;
        } else if (state.dragTarget === 'window') {
          const rangeWidth = state.dragStartRangeMax - state.dragStartRangeMin;
          let newMin = state.dragStartRangeMin + delta;
          let newMax = state.dragStartRangeMax + delta;

          // Clamp to data bounds
          if (newMin < dataMin) {
            newMin = dataMin;
            newMax = dataMin + rangeWidth;
          }
          if (newMax > dataMax) {
            newMax = dataMax;
            newMin = dataMax - rangeWidth;
          }

          state.rangeMin = newMin;
          state.rangeMax = newMax;
        }

        chart.draw();
        _fireRangeChange(chart);
        e.preventDefault();
      } else {
        // Update cursor based on hover target
        const target = hitTest(chart, px, py);
        if (target === 'left' || target === 'right') {
          setCursorStyle(chart, 'ew-resize');
        } else if (target === 'window') {
          setCursorStyle(chart, 'grab');
        } else {
          setCursorStyle(chart, 'default');
        }
      }
    };

    chart._rs_onMouseUp = (e: MouseEvent) => {
      const state = getState(chart);
      if (state.dragTarget) {
        state.dragTarget = null;
        setCursorStyle(chart, 'default');
      }
    };

    chart._rs_onDblClick = (e: MouseEvent) => {
      if (!getOpt(chart, 'enabled')) return;
      const state = getState(chart);
      if (!state || state.dataMin === null || state.dataMax === null) return;

      // Reset range to full extent
      state.rangeMin = state.dataMin;
      state.rangeMax = state.dataMax;
      chart.draw();

      const resetCallback = getOpt(chart, 'onRangeReset' as any);
      if (resetCallback) {
        resetCallback();
      }
    };

    canvas.addEventListener('mousedown', chart._rs_onMouseDown);
    canvas.addEventListener('mousemove', chart._rs_onMouseMove);
    canvas.addEventListener('dblclick', chart._rs_onDblClick);
    window.addEventListener('mouseup', chart._rs_onMouseUp);
  },

  afterUpdate(chart: any) {
    if (!getOpt(chart, 'enabled')) return;
    const state = getState(chart);
    if (!state) return;

    const extent = computeDataExtent(chart);
    if (extent) {
      state.dataMin = extent.min;
      state.dataMax = extent.max;

      // Initialize range to full extent if not set
      if (state.rangeMin === null || state.rangeMax === null) {
        state.rangeMin = extent.min;
        state.rangeMax = extent.max;
      }
    }
  },

  afterDraw(chart: any) {
    if (!getOpt(chart, 'enabled')) return;
    const state = getState(chart);
    if (!state || state.rangeMin === null || state.rangeMax === null) return;

    const xScale = getXScale(chart);
    const yScale = getYScale(chart);
    if (!xScale || !yScale) return;

    const ctx = chart.ctx;
    const chartArea = chart.chartArea;
    const leftPx = handlePixelX(chart, state.rangeMin);
    const rightPx = handlePixelX(chart, state.rangeMax);

    const overlayColor = getOpt(chart, 'overlayColor');
    const handleColor = getOpt(chart, 'handleColor');
    const handleWidth = getOpt(chart, 'handleWidth');
    const windowBorderColor = getOpt(chart, 'windowBorderColor');

    ctx.save();

    // Draw left overlay (unselected region)
    if (leftPx > chartArea.left) {
      ctx.fillStyle = overlayColor;
      ctx.fillRect(chartArea.left, chartArea.top, leftPx - chartArea.left, chartArea.bottom - chartArea.top);
    }

    // Draw right overlay (unselected region)
    if (rightPx < chartArea.right) {
      ctx.fillStyle = overlayColor;
      ctx.fillRect(rightPx, chartArea.top, chartArea.right - rightPx, chartArea.bottom - chartArea.top);
    }

    // Draw window border (top and bottom lines)
    ctx.strokeStyle = windowBorderColor;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(leftPx, chartArea.top);
    ctx.lineTo(rightPx, chartArea.top);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(leftPx, chartArea.bottom);
    ctx.lineTo(rightPx, chartArea.bottom);
    ctx.stroke();

    // Draw left handle
    _drawHandle(ctx, leftPx, chartArea.top, chartArea.bottom, handleColor, handleWidth);

    // Draw right handle
    _drawHandle(ctx, rightPx, chartArea.top, chartArea.bottom, handleColor, handleWidth);

    ctx.restore();
  },

  destroy(chart: any) {
    if (chart._rs_onMouseDown) {
      chart.canvas?.removeEventListener('mousedown', chart._rs_onMouseDown);
    }
    if (chart._rs_onMouseMove) {
      chart.canvas?.removeEventListener('mousemove', chart._rs_onMouseMove);
    }
    if (chart._rs_onDblClick) {
      chart.canvas?.removeEventListener('dblclick', chart._rs_onDblClick);
    }
    if (chart._rs_onMouseUp) {
      window.removeEventListener('mouseup', chart._rs_onMouseUp);
    }
  },

  /** Programmatically set the range (used for sync) */
  setRange(chart: any, min: number, max: number) {
    const state = getState(chart);
    if (!state) return;
    state.rangeMin = min;
    state.rangeMax = max;
    chart.draw();
  },

  /** Reset range to full extent */
  resetRange(chart: any) {
    const state = getState(chart);
    if (!state) return;
    state.rangeMin = state.dataMin;
    state.rangeMax = state.dataMax;
    chart.draw();
    _fireRangeChange(chart);
  },
};

function _drawHandle(
  ctx: CanvasRenderingContext2D,
  x: number,
  top: number,
  bottom: number,
  color: string,
  width: number
) {
  const halfWidth = width / 2;
  const height = bottom - top;
  const handleHeight = Math.min(height * 0.3, 24);
  const centerY = top + height / 2;

  // Vertical bar (full height)
  ctx.fillStyle = color;
  ctx.fillRect(x - halfWidth, top, width, height);

  // Rounded grip area in the center
  const gripWidth = width + 4;
  const gripHalf = gripWidth / 2;
  const radius = 3;

  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.roundRect(x - gripHalf, centerY - handleHeight / 2, gripWidth, handleHeight, radius);
  ctx.fill();

  // Draw grip lines (white dashes in the center)
  ctx.strokeStyle = 'rgba(255,255,255,0.8)';
  ctx.lineWidth = 1;
  const lineSpacing = 3;
  const numLines = 3;
  const startY = centerY - ((numLines - 1) * lineSpacing) / 2;
  for (let i = 0; i < numLines; i++) {
    const ly = startY + i * lineSpacing;
    ctx.beginPath();
    ctx.moveTo(x - 2, ly);
    ctx.lineTo(x + 2, ly);
    ctx.stroke();
  }
}

function _fireRangeChange(chart: any) {
  const state = getState(chart);
  const callback = getOpt(chart, 'onRangeChange' as any);
  if (callback && state.rangeMin !== null && state.rangeMax !== null) {
    callback(state.rangeMin, state.rangeMax);
  }
}

export default RangeSelectorPlugin;
